import {
  type Disease,
  RXDB_DISEASE_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/disease";
import {
  type InvestigationActivity,
  RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/investigation-activity";
import {
  type Prescription,
  RXDB_PRESCRIPTION_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/prescription";
import {
  RXDB_SYMPTOM_COLLECTION_NAME,
  type Symptom,
} from "$global-providers/rxdb-provider/collections/symptom";

export enum FHIRResourceModule {
  Diagnostic = "Diagnostic",
  Clinical = "Clinical",
}

export const EXTERNAL_CODING_SYSTEM_URI = {
  type: ["object", "null"],
  properties: {
    uri: {
      type: "string",
    },
  },
  required: ["uri"],
} as const;

export const FHIR_RESOURCE_SCHEMA = {
  type: "object",
  properties: {
    resourceType: {
      type: "object",
      properties: {
        module: {
          type: "string",
          enum: Object.values(FHIRResourceModule),
        },
        uri: { type: "string" },
      },
      required: ["module", "uri"],
    },
    externalCoding: {
      type: "object",
      properties: {
        icd10: EXTERNAL_CODING_SYSTEM_URI,
        icd11: EXTERNAL_CODING_SYSTEM_URI,
        snomed: EXTERNAL_CODING_SYSTEM_URI,
        loinc: EXTERNAL_CODING_SYSTEM_URI,
        elephantInternal: EXTERNAL_CODING_SYSTEM_URI,
      },
      required: ["icd10", "icd11", "snomed", "loinc", "elephantInternal"],
    },
    diagnosticReport: {
      type: ["object", "null"],
      properties: {
        result: {
          type: "array",
          items: {
            type: "object",
            properties: {
              value: {
                type: "object",
                properties: {
                  externalCoding: {
                    type: "object",
                    properties: {
                      snomed: EXTERNAL_CODING_SYSTEM_URI,
                    },
                    required: ["snomed"],
                  },
                },
                required: ["externalCoding"],
              },
            },
            required: ["value"],
          },
        },
      },
      required: ["result"],
    },
  },
  required: ["resourceType", "externalCoding", "diagnosticReport"],
} as const;

export type SupportedClinicalResourceTypes = {
  [RXDB_SYMPTOM_COLLECTION_NAME]: Symptom;
  [RXDB_DISEASE_COLLECTION_NAME]: Disease;
  [RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME]: InvestigationActivity;
  [RXDB_PRESCRIPTION_COLLECTION_NAME]: Prescription;
};

type FHIRResourceType = {
  module: FHIRResourceModule;
  uri: string;
};

export const FHIRResourceTypes: {
  [key in keyof SupportedClinicalResourceTypes]: FHIRResourceType;
} = {
  [RXDB_SYMPTOM_COLLECTION_NAME]: {
    uri: "http://hl7.org/fhir/observation.html",
    module: FHIRResourceModule.Diagnostic,
  },
  [RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME]: {
    uri: "http://hl7.org/fhir/diagnosticreport.html",
    module: FHIRResourceModule.Diagnostic,
  },
  [RXDB_DISEASE_COLLECTION_NAME]: {
    uri: "http://hl7.org/fhir/condition.html",
    module: FHIRResourceModule.Clinical,
  },
  [RXDB_PRESCRIPTION_COLLECTION_NAME]: {
    uri: "http://hl7.org/fhir/medication.html",
    module: FHIRResourceModule.Clinical,
  },
  // Patient: {
  //   uri: "http://hl7.org/fhir/patient.html",
  //   module: "Administrative",
  // },
};
